import React from 'react';
import { css } from '@emotion/react';
import { IconButton, MenuItem, Table, TableBody, TableHead, TableRow, TextField, Tooltip } from '@material-ui/core';
import { DeleteForeverOutlined } from '@material-ui/icons';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import NavResponsiveModal, {
    ModalActions,
    ModalContent,
    ModalHeader,
} from '@/components/Navigation/NavResponsiveModal';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import TariffLibrary from '../TariffLibrary';
import { OnwardTab, OnwardTabContainer } from '../../Tabs';
import { Body1, TabCard } from '../blocks';
import { getTariffName } from '../Tariffs/utils';
import { ALGO_TYPES, TARIFF_COLUMNS, TARIFF_TYPES } from './constants';
import { algoLabel, isDefaultTariff } from './utils';
import { useTariffTable } from './hooks';
import {
    BodyCell,
    BodyRow,
    EmptyState,
    HeaderCell,
    KindText,
    LinkAction,
    OutlinedAction,
    RowActions,
    StatusBadge,
    StatusDot,
    TableCard,
    TariffName,
    Toolbar,
    ToolbarSpacer,
} from './blocks';

// Re-exported for admin/AdminTariffs, which imported it from this module before the rewrite.
export { ALGO_TYPES };

const cardPadding = css`
    padding: 1.5rem;
`;

const libraryPadding = css`
    padding-top: 28px;
`;

// One renderer per `TARIFF_COLUMNS` key, so a column change can't shift the cells after it.
const CELLS = {
    tariff: ({ tariff, index }) => <TariffName>{getTariffName(tariff, index)}</TariffName>,
    kind: ({ tariff, tariffType }) => <KindText>{algoLabel(tariffType, tariff.algo_type)}</KindText>,
    status: ({ isDefault }) =>
        isDefault && (
            <StatusBadge linked>
                <StatusDot />
                Default
            </StatusBadge>
        ),
    actions: ({ tariff, isDefault, loading, callbacks }) => (
        <RowActions>
            {!isDefault && (
                <LinkAction type="button" disabled={loading} onClick={() => callbacks.makeDefault(tariff)}>
                    Change to default
                </LinkAction>
            )}
            <Tooltip title="Duplicate" placement="top">
                <span>
                    <IconButton
                        aria-label="Duplicate tariff"
                        disabled={loading}
                        onClick={() => callbacks.duplicate(tariff)}
                        css={css`
                            padding: 4px;
                        `}
                    >
                        <FileCopyIcon fontSize="small" color={loading ? 'disabled' : undefined} />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title="Delete" placement="top">
                <span>
                    <IconButton
                        aria-label="Delete tariff"
                        disabled={loading}
                        onClick={() => callbacks.askDelete(tariff)}
                        css={css`
                            padding: 4px;
                        `}
                    >
                        <DeleteForeverOutlined fontSize="small" color={loading ? 'disabled' : 'error'} />
                    </IconButton>
                </span>
            </Tooltip>
            <OutlinedAction onClick={() => callbacks.edit(tariff)}>Edit</OutlinedAction>
        </RowActions>
    ),
};

const TariffRow = (props) => (
    <BodyRow>
        {TARIFF_COLUMNS.map(({ key, align }) => {
            const Cell = CELLS[key];
            // Every cell but the actions opens the editor.
            const onClick = key === 'actions' ? undefined : () => props.callbacks.edit(props.tariff);
            return (
                <BodyCell key={key} align={align} onClick={onClick}>
                    <Cell {...props} />
                </BodyCell>
            );
        })}
    </BodyRow>
);

/**
 * `bare` drops the TabCard surface for callers that already provide one — same contract as
 * CustomTags. The Account pages render this unwrapped and rely on the default.
 */
const TariffTable = ({ tariffs, overrides, driverMappings, callbacks: bundleCallbacks, bare = false }) => {
    const { state, callbacks } = useTariffTable({
        tariffs,
        overrides,
        driverMappings,
        callbacks: bundleCallbacks,
    });
    const { tariffType, algo, algoTypes, tariffs: rows, defaultObj, deleting, loading } = state;

    const Surface = bare ? React.Fragment : TabCard;
    const surfaceProps = bare ? {} : { css: cardPadding };

    return (
        <>
            <Surface {...surfaceProps}>
                <OnwardTabContainer value={tariffType} onChange={(e, t) => callbacks.changeTariffType(t)}>
                    {Object.entries(TARIFF_TYPES).map(([value, label]) => (
                        <OnwardTab key={value} value={value} label={label} />
                    ))}
                </OnwardTabContainer>

                {tariffType === 'ORDER' ? (
                    <div css={libraryPadding}>
                        <TariffLibrary
                            tariffs={rows}
                            loading={loading}
                            algoTypeOptions={algoTypes}
                            onCreate={callbacks.createOrderTariff}
                            onDuplicate={(tariff_id) => callbacks.duplicate({ tariff_id })}
                            onDelete={callbacks.deleteById}
                            renderRowMeta={(tariff) => (
                                <>
                                    <span>({algoLabel(tariffType, tariff.algo_type)})</span>
                                    {isDefaultTariff(tariff, defaultObj) ? <span>(Default)</span> : null}
                                </>
                            )}
                            renderRowActions={(tariff) =>
                                isDefaultTariff(tariff, defaultObj) ? null : (
                                    <LinkAction
                                        type="button"
                                        disabled={loading}
                                        onClick={() => callbacks.makeDefault(tariff)}
                                    >
                                        Change to default
                                    </LinkAction>
                                )
                            }
                        />
                    </div>
                ) : (
                    <>
                        <Toolbar>
                            <ToolbarSpacer />
                            <TextField
                                size="small"
                                label="Type"
                                variant="outlined"
                                select
                                value={algo || ''}
                                onChange={(e) => callbacks.setAlgo(e.target.value)}
                                error={!algo}
                                css={css`
                                    width: 200px;
                                `}
                            >
                                {algoTypes.map((option) => (
                                    <MenuItem value={option.value} key={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <PrimaryButton disabled={!algo} onClick={callbacks.create}>
                                Create
                            </PrimaryButton>
                        </Toolbar>

                        <TableCard>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        {TARIFF_COLUMNS.map((column) => (
                                            <HeaderCell
                                                key={column.key}
                                                align={column.align}
                                                style={column.width ? { width: column.width } : undefined}
                                            >
                                                {column.label}
                                            </HeaderCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((tariff, index) => (
                                        <TariffRow
                                            key={tariff.tariff_id}
                                            tariff={tariff}
                                            index={index}
                                            tariffType={tariffType}
                                            isDefault={isDefaultTariff(tariff, defaultObj)}
                                            loading={loading}
                                            callbacks={callbacks}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                            {rows.length === 0 && (
                                <EmptyState>No {TARIFF_TYPES[tariffType].toLowerCase()} yet.</EmptyState>
                            )}
                        </TableCard>
                    </>
                )}
            </Surface>

            <NavResponsiveModal open={!!deleting} onClose={callbacks.cancelDelete} maxWidth="sm" fullWidth>
                <ModalHeader title="Delete Tariff?" onClose={callbacks.cancelDelete} />
                <ModalContent>
                    <Body1>Are you sure you want to delete this tariff?</Body1>
                </ModalContent>
                <ModalActions>
                    <SecondaryButton onClick={callbacks.cancelDelete}>Cancel</SecondaryButton>
                    <PrimaryButton onClick={callbacks.confirmDelete}>Delete</PrimaryButton>
                </ModalActions>
            </NavResponsiveModal>
        </>
    );
};

export default TariffTable;
